import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import Alert, { AlertColor } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

interface SnackbarContextType {
  showSnackbar: (
    type: AlertColor,
    message: string,
    autoHideAfterSeconds?: number | null,
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined,
);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    type: AlertColor;
    message: string;
    autoHideAfterSeconds?: number | null;
  }>({
    open: false,
    type: 'success', // 'success', 'error', 'warning', or 'info'
    message: '',
    autoHideAfterSeconds: 10,
  });

  const showSnackbar = useCallback(
    (
      type: AlertColor,
      message: string,
      autoHideAfterSeconds?: number | null,
    ) => {
      setSnackbar({
        open: true,
        type,
        message,
        autoHideAfterSeconds: autoHideAfterSeconds,
      });
    },
    [], // No dependencies, so the function reference will remain stable
  );

  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  function handleClose() {
    setSnackbar(prev => ({ ...prev, open: false }));
  }

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={
          snackbar.autoHideAfterSeconds
            ? snackbar.autoHideAfterSeconds * 1000
            : 10 * 1000
        }
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
